import { z } from 'zod';
import {
  ORDERED_RANGE_ISSUE,
  currencyCodeSchema,
  dateRangeFields,
  isOrderedRange,
  uuidSchema,
} from '../primitives.js';
import {
  EXPORT_FORMATS,
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  REPORT_DIMENSIONS,
  REPORT_PERIODS,
  SALE_SOURCES,
} from '../enums.js';

/**
 * Which currency a set of figures is stated in, and what else was present.
 *
 * A report states exactly ONE currency and filters its aggregates to it, so
 * amounts in different currencies are never summed together. `present` lists
 * every currency with records in the window, so a business trading in two of
 * them can see that the other exists rather than silently losing it.
 *
 * For a single-currency business `present` has one entry and `isMixed` is
 * false, which is the case today for every business.
 */
export const reportCurrencySchema = z.object({
  code: z.string(),
  present: z.array(z.string()),
  isMixed: z.boolean(),
});
export type ReportCurrency = z.infer<typeof reportCurrencySchema>;

// ── Dashboard ───────────────────────────────────────────────────────────────

export const dashboardQuerySchema = z.object({
  branchId: uuidSchema.optional(),
});

/**
 * What a business earned over a window, from every place money is recorded,
 * counted once. The same figures the dashboard, the reports and the assistant
 * are given.
 *
 * ── Revenue is three things, never one ──────────────────────────────────────
 *
 * A shop sale and a dispatch fee are different trades and must not be added
 * into one number without saying so. A ₦3,000 delivery on which the rider
 * keeps ₦2,500 earns the company ₦500 — so ₦500 is revenue and ₦3,000 is
 * throughput. Adding the fee would triple the apparent size of a courier.
 *
 *   totalRevenue = shopRevenue + deliveryCommission + riderRemittanceRevenue
 *
 * `grossDeliveryFees`, `riderPayouts` and `deliveries` are operational: real,
 * worth showing, and deliberately NOT part of that sum.
 */
export const growthTotalsSchema = z.object({
  /** Sales that are not a delivery's own posting. Goods, not dispatch. */
  shopRevenue: z.string(),
  /** Company share of deliveries: booked where the source stated one. */
  deliveryCommission: z.string(),
  /**
   * The agreed weekly charge a rider owes for working. Income, and its own
   * line so it is never mistaken for commission earned on rides.
   *
   * Explicitly NOT the cash a rider holds on the company's behalf — that is
   * `company_share_held_by_rider` on a settlement, a cash position rather than
   * income, and it is not read here at all.
   */
  riderRemittanceRevenue: z.string(),
  /** The three above. The only figure that is "revenue" without qualification. */
  totalRevenue: z.string(),

  /** Operational — scale, not income. The fees customers paid, in full. */
  grossDeliveryValue: z.string(),
  riderPayouts: z.string(),
  /**
   * Cash the company holds out of the fares: `fee - rider_payout`, always.
   *
   * Distinct from `deliveryCommission`, which is what gets RECOGNISED — the
   * source's own booked figure where it stated one. The two agree on every
   * ride whose arithmetic closes; where they differ, the difference is an
   * unexplained gap in the source and is deliberately not revenue. One
   * staging row behaves this way: ₦850 held against ₦150 booked.
   */
  companyKept: z.string(),
  /** Delivered and paid. */
  deliveries: z.number(),
  /** Distinct customers with a counted sale or delivery. */
  customers: z.number(),
});
export type GrowthTotals = z.infer<typeof growthTotalsSchema>;

/**
 * The dashboard summary. "Today" is computed in the *business's* timezone, not
 * the server's — a Lagos restaurant closing at 11pm must not see its takings
 * jump to tomorrow because the server runs in UTC.
 */
export const dashboardSummarySchema = z.object({
  currencyCode: z.string(),
  currency: reportCurrencySchema,
  timezone: z.string(),
  date: z.string(),
  today: z.object({
    /** Gross sales made today. */
    sales: z.string(),
    /** Refunds given today. `profit` is net of them. */
    refunds: z.string(),
    expenses: z.string(),
    /** sales − refunds − expenses: Reports' operating profit for the day. */
    profit: z.string(),
    transactionCount: z.number(),
    /** Total still owed across *all* open sales, not just today's. */
    outstandingPayments: z.string(),
    outstandingCount: z.number(),
  }),
  /** Same figures for the previous day, so the UI can show a delta. */
  yesterday: z.object({
    sales: z.string(),
    refunds: z.string(),
    expenses: z.string(),
    profit: z.string(),
    transactionCount: z.number(),
  }),
  /**
   * Today's revenue, split the way the business actually earns it.
   *
   * From the SAME analytics service the growth card and the assistant call, so
   * the three cannot disagree about one day. `today.sales` above is the sales
   * ledger alone and stays as it was — it is a till figure, not revenue, and a
   * delivery business reading it saw NGN 0 against a full delivery book.
   */
  revenue: growthTotalsSchema,
  /** Yesterday's, for the deltas. */
  previousRevenue: growthTotalsSchema,
  /** Last 14 days, oldest first — powers the sparkline. */
  trend: z.array(
    z.object({
      date: z.string(),
      sales: z.string(),
      refunds: z.string(),
      expenses: z.string(),
      profit: z.string(),
      transactionCount: z.number(),
    }),
  ),
});
export type DashboardSummary = z.infer<typeof dashboardSummarySchema>;

// ── Company growth ──────────────────────────────────────────────────────────

export const GROWTH_RANGES = ['24H', '7D', '1M', '3M', 'YTD', '1Y', 'MAX'] as const;
export type GrowthRange = (typeof GROWTH_RANGES)[number];

/** Which line the growth chart draws. Each maps to one field of the totals. */
export const GROWTH_METRICS = [
  'totalRevenue',
  'shopRevenue',
  'logisticsRevenue',
  'deliveryCommission',
  'riderRemittanceRevenue',
  'deliveries',
] as const;
export type GrowthMetric = (typeof GROWTH_METRICS)[number];

export const GROWTH_METRIC_LABELS: Record<GrowthMetric, string> = {
  totalRevenue: 'Total revenue',
  shopRevenue: 'Shop',
  logisticsRevenue: 'Logistics',
  deliveryCommission: 'Commission',
  riderRemittanceRevenue: 'Remittance',
  deliveries: 'Deliveries',
};

export const growthQuerySchema = z.object({
  range: z.enum(GROWTH_RANGES).default('1M'),
  branchId: uuidSchema.optional(),
});



const growthWindowSchema = z.object({
  from: z.string(),
  to: z.string(),
  startsAt: z.string(),
  endsAt: z.string(),
});

export const growthSummarySchema = z.object({
  range: z.enum(GROWTH_RANGES),
  grain: z.enum(['hour', 'day', 'week', 'month']),
  currencyCode: z.string(),
  timezone: z.string(),
  current: growthWindowSchema,
  /** The equivalent window before; null for MAX, which has none. */
  previous: growthWindowSchema.nullable(),
  totals: growthTotalsSchema,
  previousTotals: growthTotalsSchema.nullable(),
  /** Oldest first. `bucket` is a local date, or a local hour for 24H. */
  points: z.array(growthTotalsSchema.extend({ bucket: z.string() })),
});
export type GrowthSummary = z.infer<typeof growthSummarySchema>;

// ── Reports ─────────────────────────────────────────────────────────────────

export const reportQuerySchema = dateRangeFields
  .extend({
    period: z.enum(REPORT_PERIODS).default('daily'),
    branchId: uuidSchema.optional(),
    /** Narrow the whole report to one channel. */
    channelId: uuidSchema.optional(),
    /**
     * State the report in this currency. Defaults to the business's own when
     * the window contains it, else the currency carrying the most value.
     * This is a filter, not a conversion — no FX is applied.
     */
    currencyCode: currencyCodeSchema.optional(),
  })
  .refine(isOrderedRange, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });

export type ReportQuery = z.infer<typeof reportQuerySchema>;

/**
 * A figure alongside the same figure for the previous equivalent period.
 *
 * `changePercent` is null when the previous value was zero: "revenue grew by
 * infinity%" is not a statement anyone can act on, and the UI renders "new"
 * instead. Money stays a decimal string throughout; only the percentage — a
 * ratio, never an amount — is a number.
 */
const moneyGrowthSchema = z.object({
  current: z.string(),
  previous: z.string(),
  changeAmount: z.string(),
  changePercent: z.number().nullable(),
  direction: z.enum(['up', 'down', 'flat']),
});
export type MoneyGrowth = z.infer<typeof moneyGrowthSchema>;

const countGrowthSchema = z.object({
  current: z.number(),
  previous: z.number(),
  changeAmount: z.number(),
  changePercent: z.number().nullable(),
  direction: z.enum(['up', 'down', 'flat']),
});
export type CountGrowth = z.infer<typeof countGrowthSchema>;

const breakdownRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  amount: z.string(),
  count: z.number(),
  /** Share of the total, 0–100, or null when the total is zero. */
  percentage: z.number().nullable(),
  /** Same figure in the previous equivalent period, for growth arrows. */
  previousAmount: z.string().nullable(),
  changePercent: z.number().nullable(),
  /**
   * Dimension and key needed to drill into this row. Present whenever the row
   * is drillable; null for synthetic rows that have no underlying filter.
   */
  dimension: z.enum(REPORT_DIMENSIONS).nullable(),
});
export type BreakdownRow = z.infer<typeof breakdownRowSchema>;

const seriesPointSchema = z.object({
  /** Start of the bucket, in the business timezone. Date, or ISO hour. */
  bucket: z.string(),
  label: z.string(),
  revenue: z.string(),
  /** Refunds given in this bucket. operatingProfit is net of them. */
  refunds: z.string(),
  collected: z.string(),
  expenses: z.string(),
  grossProfit: z.string().nullable(),
  operatingProfit: z.string(),
  transactionCount: z.number(),
});
export type SeriesPoint = z.infer<typeof seriesPointSchema>;

/**
 * The headline figures.
 *
 * `netProfit` is operating profit (net revenue − expenses) adjusted for the loan
 * lines below it — finance income, finance expense and bad debts — and is what
 * the reports lead with, because it is always computable. For a business with
 * no loans it equals `operatingProfit` exactly. `grossProfit` remains null-when-unknown rather than assuming a
 * zero cost — see the note on the reports service.
 */
const reportTotalsSchema = z.object({
  /** Gross sales made in the period — what the breakdowns sum to. */
  revenue: z.string(),
  /**
   * Refunds given in the period, by when they were given. Each is money paid
   * back, so it reduces both revenue (→ netRevenue) and cash (→ netCollected).
   */
  refunds: z.string(),
  /** revenue − refunds. What operating profit is measured from. */
  netRevenue: z.string(),
  /**
   * Of the refunds, the money actually paid back. The rest were credit notes
   * against unpaid balances, which reverse revenue but move no money.
   */
  refundsPaidOut: z.string(),
  /** collected − refundsPaidOut: money that came in and stayed. */
  netCollected: z.string(),
  /** Cost of goods reversed because returned stock went back on sale. */
  restockedCost: z.string(),
  /** Money actually received in the period — sum of sale_payments. */
  collected: z.string(),
  /** Still owed on sales in this period. */
  outstanding: z.string(),
  expenses: z.string(),
  grossProfit: z.string().nullable(),
  grossProfitCoverage: z.number().nullable(),
  /** netRevenue − expenses. Always available. Loans never reach it. */
  operatingProfit: z.string(),
  /**
   * Below operating profit, from the loan ledger:
   *   financeIncome  — interest and fees received on money lent;
   *   financeExpense — interest and fees paid on money borrowed;
   *   badDebts       — loan principal written off.
   * Principal itself never appears: lending and repayment are balance-sheet
   * movements. Zero for a business with no loans, and for a channel-filtered
   * report, since loans belong to no channel.
   */
  financeIncome: z.string(),
  financeExpense: z.string(),
  badDebts: z.string(),
  /** operatingProfit + financeIncome − financeExpense − badDebts. */
  netProfit: z.string(),
  transactionCount: z.number(),
  averageTransaction: z.string(),
  /** Distinct customers who bought in the period. */
  customerCount: z.number(),
  /** First-ever purchase falls inside the period. */
  newCustomerCount: z.number(),
  /** Bought in the period, but had bought before it too. */
  returningCustomerCount: z.number(),
});
export type ReportTotals = z.infer<typeof reportTotalsSchema>;

const comparisonSchema = z.object({
  /** The window the current figures are measured against. */
  range: z.object({ from: z.string(), to: z.string() }),
  label: z.string(),
  revenue: moneyGrowthSchema,
  collected: moneyGrowthSchema,
  expenses: moneyGrowthSchema,
  netProfit: moneyGrowthSchema,
  transactionCount: countGrowthSchema,
  averageTransaction: moneyGrowthSchema,
  customerCount: countGrowthSchema,
});
export type ReportComparison = z.infer<typeof comparisonSchema>;

// ── Narrative ("AI Business Summary") ───────────────────────────────────────

/**
 * Plain-language explanation of the period.
 *
 * Every sentence is derived from the same aggregates the tiles are rendered
 * from, so the prose can never contradict the numbers beside it. Each insight
 * carries the dimension and key it was derived from, which is what lets the UI
 * make a sentence clickable straight through to the transactions behind it.
 */
const insightSchema = z.object({
  text: z.string(),
  tone: z.enum(['positive', 'negative', 'neutral']),
  /** What this sentence is about, for iconography and ordering. */
  kind: z.enum([
    'revenue',
    'expenses',
    'profit',
    'channel',
    'branch',
    'product',
    'customer',
    'outstanding',
    'coverage',
  ]),
  dimension: z.enum(REPORT_DIMENSIONS).nullable(),
  dimensionKey: z.string().nullable(),
});
export type Insight = z.infer<typeof insightSchema>;

export const businessNarrativeSchema = z.object({
  headline: z.string(),
  insights: z.array(insightSchema),
  /** False when the period has no activity at all — the UI shows guidance. */
  hasActivity: z.boolean(),
  generatedAt: z.string(),
});
export type BusinessNarrative = z.infer<typeof businessNarrativeSchema>;

// ── Report summary ──────────────────────────────────────────────────────────

export const reportSummarySchema = z.object({
  /** The currency every figure below is in. Same as `currency.code`. */
  currencyCode: z.string(),
  currency: reportCurrencySchema,
  timezone: z.string(),
  period: z.enum(REPORT_PERIODS),
  range: z.object({ from: z.string(), to: z.string() }),
  comparison: comparisonSchema,
  totals: reportTotalsSchema,
  series: z.array(seriesPointSchema),

  byChannel: z.array(breakdownRowSchema),
  byBranch: z.array(breakdownRowSchema),
  byProduct: z.array(breakdownRowSchema),
  byPaymentMethod: z.array(breakdownRowSchema),
  byPaymentStatus: z.array(breakdownRowSchema),
  byCustomer: z.array(breakdownRowSchema),
  byStaff: z.array(breakdownRowSchema),
  byExpenseCategory: z.array(breakdownRowSchema),

  narrative: businessNarrativeSchema,
});
export type ReportSummary = z.infer<typeof reportSummarySchema>;

// ── Drill-down ──────────────────────────────────────────────────────────────

/**
 * "Show me the transactions behind this number."
 *
 * The contract that makes the whole reporting layer trustworthy: every figure
 * on screen can be expanded into the exact rows that produced it, and the
 * returned `totalAmount` is asserted to equal the figure that was clicked.
 */
export const drilldownQuerySchema = dateRangeFields
  .extend({
    period: z.enum(REPORT_PERIODS).default('daily'),
    branchId: uuidSchema.optional(),
    channelId: uuidSchema.optional(),
    /**
     * Must match the currency of the summary figure being opened, so the rows
     * reconcile against it. The report states its currency in `currency.code`;
     * the client passes that straight back.
     */
    currencyCode: currencyCodeSchema.optional(),
    /**
     * Which slice to open. Omitted entirely means "everything in the period",
     * which is what clicking the headline Revenue or Expenses tile does.
     */
    dimension: z.enum(REPORT_DIMENSIONS).optional(),
    /** The breakdown row's key. Required when `dimension` is given. */
    key: z.string().max(128).optional(),
    /**
     * Which ledger to open. Expense dimensions force `expenses`.
     *
     * The derived kinds exist so that a figure which is not simply "a list of
     * sales" can still open the rows that produce it, and still reconcile:
     *
     *  - `outstanding`  sales still owing, carrying the OUTSTANDING PORTION as
     *                   the row amount rather than the full sale value. Both
     *                   unpaid and partially-paid sales qualify — a half-paid
     *                   invoice is money owed.
     *  - `profit`       sales as positive rows and expenses as negative ones,
     *                   so the list totals to exactly revenue − expenses.
     *  - `gross_profit` only sales carrying cost data, each row showing its
     *                   own margin, so the total is the reported gross profit
     *                   and the coverage caveat is visible row by row.
     */
    kind: z
      .enum(['sales', 'expenses', 'payments', 'outstanding', 'profit', 'gross_profit'])
      .default('sales'),
    /**
     * Narrow a sales drill-down to a customer cohort, so the "new" and
     * "returning" customer tiles can open the trade they represent.
     */
    customers: z.enum(['all', 'new', 'returning']).default('all'),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(200).default(50),
  })
  .refine(isOrderedRange, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] })
  .refine((q) => !q.dimension || q.key !== undefined, {
    message: 'key is required when dimension is given',
    path: ['key'],
  });
export type DrilldownQuery = z.infer<typeof drilldownQuerySchema>;

const drilldownRowSchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  occurredAt: z.string(),
  amount: z.string(),
  description: z.string().nullable(),
  counterparty: z.string().nullable(),
  channel: z.string().nullable(),
  branch: z.string().nullable(),
  product: z.string().nullable(),
  staff: z.string().nullable(),
  paymentMethod: z.string().nullable(),
  paymentStatus: z.string().nullable(),
  amountPaid: z.string().nullable(),
  amountOutstanding: z.string().nullable(),
});
export type DrilldownRow = z.infer<typeof drilldownRowSchema>;

export const drilldownResultSchema = z.object({
  /** The currency these rows are in. Every row shares it — never mixed. */
  currencyCode: z.string(),
  currency: reportCurrencySchema,
  timezone: z.string(),
  kind: z.enum(['sales', 'expenses', 'payments', 'outstanding', 'profit', 'gross_profit']),
  dimension: z.enum(REPORT_DIMENSIONS).nullable(),
  key: z.string().nullable(),
  label: z.string(),
  range: z.object({ from: z.string(), to: z.string() }),
  /**
   * Sum over EVERY matching row, not just the page. This is the number that
   * must equal the summary figure the user clicked — paginating a drill-down
   * must never change the total it reconciles to.
   */
  totalAmount: z.string(),
  totalCount: z.number(),
  /**
   * Distinct customers across every matching row, not just the page.
   *
   * This is what lets the Customers / New / Returning tiles reconcile: their
   * headline is a count of people, not of transactions, so a row count could
   * never agree with them.
   */
  distinctCustomerCount: z.number(),
  /** What the row amounts mean, so the modal can caption the column honestly. */
  amountMeaning: z.enum([
    'sale_value',
    'expense_value',
    'payment_received',
    'outstanding_balance',
    'signed_contribution',
    'gross_margin',
  ]),
  page: z.number(),
  pageSize: z.number(),
  rows: z.array(drilldownRowSchema),
});
export type DrilldownResult = z.infer<typeof drilldownResultSchema>;

// ── Sales channels ──────────────────────────────────────────────────────────

export const createChannelSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(280).nullable().optional(),
  isActive: z.boolean().default(true),
});
export type CreateChannelInput = z.infer<typeof createChannelSchema>;

export const updateChannelSchema = createChannelSchema.partial();
export type UpdateChannelInput = z.infer<typeof updateChannelSchema>;

export const salesChannelSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  description: z.string().nullable(),
  /**
   * True for the channels seeded with every business. They can be renamed and
   * deactivated but never deleted — historical sales point at them.
   */
  isSystem: z.boolean(),
  isActive: z.boolean(),
  /** Legacy `sales.source` value this channel corresponds to, when any. */
  source: z.enum(SALE_SOURCES).nullable(),
  createdAt: z.string(),
});
export type SalesChannel = z.infer<typeof salesChannelSchema>;

// ── Products / services ─────────────────────────────────────────────────────

export const createProductSchema = z.object({
  name: z.string().trim().min(1).max(120),
  sku: z.string().trim().max(60).nullable().optional(),
  description: z.string().trim().max(280).nullable().optional(),
  unitPrice: z
    .string()
    .regex(/^\d{1,16}(\.\d{1,2})?$/, 'Unit price must be a decimal amount')
    .nullable()
    .optional(),
  isActive: z.boolean().default(true),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const updateProductSchema = createProductSchema.partial();
export type UpdateProductInput = z.infer<typeof updateProductSchema>;

export const productSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sku: z.string().nullable(),
  description: z.string().nullable(),
  unitPrice: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type Product = z.infer<typeof productSchema>;

// ── Exports ─────────────────────────────────────────────────────────────────

export const EXPORT_DATASETS = [
  'sales',
  'expenses',
  'customers',
  'staff',
  'report_summary',
  'report_breakdowns',
  // The loan book and its ledger. Also need `loans:read`.
  'loans',
  'loan_repayments',
] as const;
export type ExportDataset = (typeof EXPORT_DATASETS)[number];

export const exportQuerySchema = dateRangeFields
  .extend({
    dataset: z.enum(EXPORT_DATASETS),
    format: z.enum(EXPORT_FORMATS).default('xlsx'),
    period: z.enum(REPORT_PERIODS).default('daily'),
    branchId: uuidSchema.optional(),
    channelId: uuidSchema.optional(),
    /**
     * State a report export in this currency, the same way the on-screen
     * report does. The client passes back whatever `currency.code` the report
     * it is looking at reported, so the file and the screen agree. Ignored by
     * the row-level datasets, which carry a currency column per row.
     */
    currencyCode: currencyCodeSchema.optional(),
    source: z.enum(SALE_SOURCES).optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
  })
  .refine(isOrderedRange, { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });

export type ExportQuery = z.infer<typeof exportQuerySchema>;
